// src/pages/LoginPage.jsx
import React, { useMemo, useState } from "react";
import { useAuth } from "../auth/AuthContext";

const LoginPage = () => {
	const { login } = useAuth();

	const [form, setForm] = useState({ email: "", password: "" });
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState("");
	const [showPassword, setShowPassword] = useState(false);

	const canSubmit = useMemo(() => {
		const email = String(form.email || "").trim();
		const password = String(form.password || "");
		return email.length > 0 && password.length > 0 && !submitting;
	}, [form.email, form.password, submitting]);

	const onChange = (e) => {
		const { name, value } = e.target;
		setError("");
		setForm((prev) => ({ ...prev, [name]: value }));
	};

	const onSubmit = async (e) => {
		e.preventDefault();
		if (!canSubmit) return;

		setSubmitting(true);
		setError("");

		try {
			await login({
				email: String(form.email || "").trim(),
				password: form.password
			});
		} catch (err) {
			console.error("Login failed:", err);
			setError("Invalid email or password.");
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<div className="min-h-screen bg-slate-50">
			<div className="mx-auto flex min-h-screen items-center justify-center px-4">
				<div className="w-full max-w-md">
					{/* card bg -> rgb(6 34 59 / var(--tw-bg-opacity, 1)) */}
					<div
						className="rounded-2xl border border-white/10 p-6 shadow-sm sm:p-8"
						style={{ backgroundColor: "rgb(6 34 59 / 1)" }}
					>
						{/* LOGO */}
						<div className="mb-6 flex justify-center">
							<div className="grid h-14 w-auto place-items-center">
								<img
									src="./fconnect33_Grey.png"
									alt="Company Logo"
									className="h-14 w-auto"
								/>
							</div>
						</div>

						<div className="mb-6 text-center">
							<h2 className="text-2xl font-bold text-white">Welcome back</h2>
							<p className="mt-1 text-sm text-white/70">
								Sign in to view your latest insights.
							</p>
						</div>

						<form
							onSubmit={onSubmit}
							className="space-y-4 flex flex-col items-center"
						>
							<div className="w-full">
								<label className="mb-1 block text-xs font-medium uppercase tracking-wide text-white/70">
									Email
								</label>
								<input
									type="email"
									name="email"
									autoComplete="email"
									className="w-full rounded-xl border border-white/10 bg-white/95 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-white/20 focus:ring-4 focus:ring-white/10"
									value={form.email}
									onChange={onChange}
									required
								/>
							</div>

							<div className="w-full">
								<label className="mb-1 block text-xs font-medium uppercase tracking-wide text-white/70">
									Password
								</label>

								<div className="relative">
									<input
										type={showPassword ? "text" : "password"}
										name="password"
										autoComplete="current-password"
										className="w-full rounded-xl border border-white/10 bg-white/95 px-4 py-3 pr-14 text-sm text-slate-900 outline-none transition focus:border-white/20 focus:ring-4 focus:ring-white/10"
										value={form.password}
										onChange={onChange}
										required
									/>
									<button
										type="button"
										onClick={() => setShowPassword((s) => !s)}
										className="absolute inset-y-0 right-2 my-2 rounded-lg px-3 text-xs font-semibold"
										style={{ color: "rgb(6 34 59 / 1)" }}
									>
										{showPassword ? "Hide" : "Show"}
									</button>
								</div>
							</div>

							{error && (
								<div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
									{error}
								</div>
							)}

							{/* button bg -> rgb(16 185 129 / var(--tw-text-opacity, 1)) */}
							<button
								type="submit"
								disabled={!canSubmit}
								className="mt-2 w-full max-w-[200px] rounded-xl px-4 py-3 text-sm font-semibold text-white shadow-sm transition disabled:cursor-not-allowed disabled:opacity-60"
								style={{ backgroundColor: "rgb(16 185 129 / 1)" }}
							>
								{submitting ? "Signing in..." : "Sign in"}
							</button>
						</form>
					</div>
				</div>
			</div>
		</div>
	);
};

export default LoginPage;
